import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  useTheme,
} from "@mui/material";
import { tokens } from "../../theme";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";

export default function DataTable({
  columns,
  data,
  onView,
  onEdit,
  customActions,
  loading = false,
  emptyMessage = "No data available",
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 4,
        }}
      >
        <Typography variant="body1" sx={{ color: colors.grey[400] }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 4,
        }}
      >
        <Typography variant="body1" sx={{ color: colors.grey[400] }}>
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        width: "100%",
        minWidth: "100%",
        maxWidth: "100%",
        flex: 1,
        boxSizing: "border-box",
        background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
        border: `1px solid ${colors.grey[800]}30`,
        borderRadius: "16px",
        overflow: "hidden",
      }}
    >
      <Table 
        sx={{ 
          width: "100%",
          tableLayout: "fixed",
          minWidth: "100%",
        }}
      >
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: `${colors.primary[600]}80`,
              borderBottom: `2px solid ${colors.grey[800]}30`,
            }}
          >
            {columns.map((column) => (
              <TableCell
                key={column.id}
                sx={{
                  color: colors.grey[100],
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {column.label}
              </TableCell>
            ))}
            {(onView || onEdit || customActions) && (
              <TableCell
                sx={{
                  color: colors.grey[100],
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  textAlign: "center",
                }}
              >
                Actions
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow
              key={row.id || index}
              sx={{
                "&:hover": {
                  backgroundColor: `${colors.primary[600]}30`,
                },
                borderBottom: `1px solid ${colors.grey[800]}20`,
              }}
            >
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{
                    color: colors.grey[200],
                    fontSize: "0.875rem",
                  }}
                >
                  {column.format
                    ? column.format(row[column.id], row)
                    : row[column.id] || "-"}
                </TableCell>
              ))}
              {(onView || onEdit || customActions) && (
                <TableCell
                  sx={{
                    textAlign: "center",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                    {customActions ? (
                      customActions(row)
                    ) : (
                      <>
                    {onView && (
                      <Tooltip title="View">
                        <IconButton
                          size="small"
                          onClick={() => onView(row)}
                          sx={{
                            color: colors.blueAccent[400],
                            "&:hover": {
                              backgroundColor: `${colors.blueAccent[800]}30`,
                            },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {onEdit && (
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => onEdit(row)}
                          sx={{
                            color: colors.greenAccent[400],
                            "&:hover": {
                              backgroundColor: `${colors.greenAccent[800]}30`,
                            },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                        )}
                      </>
                    )}
                  </Box>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

